import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  getArticleRedeemCodeList,
  getArticleRedeemCodeById,
  createArticleRedeemCode,
  updateArticleRedeemCode,
  deleteArticleRedeemCode
} from '../../api/articleRedeemCode';
import { message } from 'antd';

export const fetchArticleRedeemCodeList = createAsyncThunk(
  'articleRedeemCode/fetchList',
  async (queryDTO = {}, { rejectWithValue }) => {
    try {
      const response = await getArticleRedeemCodeList(queryDTO);
      if (response && response.code === 200) {
        return {
          data: response.data,
          params: queryDTO
        };
      }
      return rejectWithValue(response?.message || '获取兑换码列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取兑换码列表失败');
    }
  }
);

export const fetchArticleRedeemCodeById = createAsyncThunk(
  'articleRedeemCode/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getArticleRedeemCodeById(id);
      if (response && response.code === 200) {
        return response.data;
      }
      return rejectWithValue(response?.message || '获取兑换码详情失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取兑换码详情失败');
    }
  }
);

export const createArticleRedeemCodeAsync = createAsyncThunk(
  'articleRedeemCode/create',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await createArticleRedeemCode(data);
      if (response && response.code === 200) {
        message.success('创建兑换码成功');
        dispatch(fetchArticleRedeemCodeList({ page: 1, size: 10 }));
        return response.data;
      }
      return rejectWithValue(response?.message || '创建兑换码失败');
    } catch (error) {
      const errorMessage = error.response?.data?.message || '创建兑换码失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateArticleRedeemCodeAsync = createAsyncThunk(
  'articleRedeemCode/update',
  async ({ id, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await updateArticleRedeemCode(id, data);
      if (response && response.code === 200) {
        message.success('更新兑换码成功');
        dispatch(fetchArticleRedeemCodeList({ page: 1, size: 10 }));
        return response.data;
      }
      return rejectWithValue(response?.message || '更新兑换码失败');
    } catch (error) {
      const errorMessage = error.response?.data?.message || '更新兑换码失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteArticleRedeemCodeAsync = createAsyncThunk(
  'articleRedeemCode/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await deleteArticleRedeemCode(id);
      if (response && response.code === 200) {
        message.success('删除兑换码成功');
        dispatch(fetchArticleRedeemCodeList({ page: 1, size: 10 }));
        return response.data;
      }
      return rejectWithValue(response?.message || '删除兑换码失败');
    } catch (error) {
      const errorMessage = error.response?.data?.message || '删除兑换码失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const initialState = {
  articleRedeemCodeList: {
    data: [],
    total: 0,
    current: 1,
    size: 10,
    loading: false,
    searchParams: {}
  },
  currentArticleRedeemCode: null,
  operationStatus: {
    loading: false,
    success: false,
    error: null
  }
};

const articleRedeemCodeSlice = createSlice({
  name: 'articleRedeemCode',
  initialState,
  reducers: {
    clearCurrentArticleRedeemCode: (state) => {
      state.currentArticleRedeemCode = null;
    },
    setSearchParams: (state, action) => {
      const { page, size, ...searchParams } = action.payload;
      if (page !== undefined) {
        state.articleRedeemCodeList.current = page;
      }
      if (size !== undefined) {
        state.articleRedeemCodeList.size = size;
      }
      state.articleRedeemCodeList.searchParams = {
        ...state.articleRedeemCodeList.searchParams,
        ...searchParams
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchArticleRedeemCodeList.pending, (state) => {
        state.articleRedeemCodeList.loading = true;
      })
      .addCase(fetchArticleRedeemCodeList.fulfilled, (state, action) => {
        state.articleRedeemCodeList.loading = false;
        state.articleRedeemCodeList.data = action.payload.data.records || [];
        state.articleRedeemCodeList.total = action.payload.data.total || 0;
        state.articleRedeemCodeList.current = action.payload.data.current || action.payload.params.page || 1;
        if (action.payload.params) {
          state.articleRedeemCodeList.searchParams = {
            ...state.articleRedeemCodeList.searchParams,
            ...action.payload.params
          };
        }
      })
      .addCase(fetchArticleRedeemCodeList.rejected, (state, action) => {
        state.articleRedeemCodeList.loading = false;
        message.error(action.payload || '获取兑换码列表失败');
      })
      .addCase(fetchArticleRedeemCodeById.pending, (state) => {
        state.operationStatus.loading = true;
      })
      .addCase(fetchArticleRedeemCodeById.fulfilled, (state, action) => {
        state.operationStatus.loading = false;
        state.currentArticleRedeemCode = action.payload;
      })
      .addCase(fetchArticleRedeemCodeById.rejected, (state, action) => {
        state.operationStatus.loading = false;
        message.error(action.payload || '获取兑换码详情失败');
      })
      .addCase(createArticleRedeemCodeAsync.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
      })
      .addCase(createArticleRedeemCodeAsync.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(createArticleRedeemCodeAsync.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.error = action.payload;
      })
      .addCase(updateArticleRedeemCodeAsync.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
      })
      .addCase(updateArticleRedeemCodeAsync.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(updateArticleRedeemCodeAsync.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.error = action.payload;
      })
      .addCase(deleteArticleRedeemCodeAsync.pending, (state) => {
        state.operationStatus.loading = true;
      })
      .addCase(deleteArticleRedeemCodeAsync.fulfilled, (state) => {
        state.operationStatus.loading = false;
      })
      .addCase(deleteArticleRedeemCodeAsync.rejected, (state) => {
        state.operationStatus.loading = false;
      });
  }
});

export const { clearCurrentArticleRedeemCode, setSearchParams } = articleRedeemCodeSlice.actions;
export default articleRedeemCodeSlice.reducer;